import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Editor } from "@tinymce/tinymce-react";
import Swal from "sweetalert2";
import AdminNavbar from "./AdminNavbar";

const API_URL = process.env.REACT_APP_API_URL;
const TINYMCE_KEY = process.env.REACT_APP_TINYMCE_API_KEY;

export default function CreateProject() {
  const navigate = useNavigate();
  const editorRef = useRef(null);
  const [form, setForm] = useState({ name: "", description: "", date: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.id]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const content = editorRef.current ? editorRef.current.getContent() : "";

    const data = new FormData();
    data.append("name", form.name);
    data.append("description", form.description);
    data.append("date", form.date);
    data.append("content", content);
    if (file) data.append("thumbnail", file);

    try {
      const res = await fetch(`${API_URL}/api/projects`, {
        method: "POST",
        body: data,
      });
      if (!res.ok) throw new Error("Failed");
      await Swal.fire({
        icon: "success",
        title: "Published.",
        text: "Project saved successfully.",
        confirmButtonColor: "#DC2626",
      });
      navigate("/ProjectsDashboard");
    } catch {
      Swal.fire({
        icon: "error",
        title: "Save failed",
        text: "Please try again.",
        confirmButtonColor: "#DC2626",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="admin-shell">
      <AdminNavbar />

      <div className="admin-page">
        <div className="admin-head">
          <div>
            <p className="eyebrow">New</p>
            <h1>Document a <em>project</em>.</h1>
          </div>
        </div>

        <form className="admin-form wide" onSubmit={handleSubmit}>
          <div className="field">
            <label htmlFor="name">Project Name</label>
            <input id="name" type="text" placeholder="Project title"
              value={form.name} onChange={handleChange} required />
          </div>

          <div className="field">
            <label htmlFor="description">Short Description</label>
            <textarea id="description" rows="3" placeholder="One-line summary..."
              value={form.description} onChange={handleChange} required />
          </div>

          <div className="field">
            <label htmlFor="date">Date</label>
            <input id="date" type="date"
              value={form.date} onChange={handleChange} required />
          </div>

          <div className="field">
            <label>Thumbnail</label>
            <label className="file-drop">
              {file ? <><strong>{file.name}</strong> — click to replace</>
                    : <>Drop or <strong>choose an image</strong></>}
              <input type="file" accept="image/*"
                onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </label>
          </div>

          <div className="field">
            <label>Case Study / Body</label>
            <div className="editor-wrap">
              <Editor
                apiKey={TINYMCE_KEY}
                onInit={(_, editor) => (editorRef.current = editor)}
                init={{
                  height: 780,
                  min_height: 600,
                  menubar: "file edit view insert format tools",
                  plugins:
                    "link image lists code table quickbars autolink fullscreen media searchreplace preview wordcount",
                  toolbar:
                    "undo redo | blocks | bold italic underline strikethrough | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link image media blockquote | table | code preview | fullscreen",
                  resize: true,
                  branding: false,
                  statusbar: true,
                  elementpath: false,

                  // Send pasted/dragged images to Cloudinary via our backend
                  // instead of inlining them as base64. Keeps saved project
                  // payloads small (Vercel caps request bodies at 4.5 MB).
                  images_upload_url: `${API_URL}/api/upload/inline`,
                  automatic_uploads: true,
                  paste_data_images: true,
                  images_reuse_filename: true,

                  content_style: `
                    body {
                      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                      font-size: 17px;
                      line-height: 1.8;
                      color: #0a0a0a;
                      padding: 1.5rem clamp(1rem, 5vw, 3rem);
                      max-width: 900px;
                      margin: 0 auto;
                    }
                    p { margin: 0 0 1.4rem; }
                    h1, h2, h3, h4 {
                      font-family: 'Instrument Serif', Georgia, serif;
                      font-weight: 400;
                      letter-spacing: -0.015em;
                      line-height: 1.2;
                      color: #0a0a0a;
                      margin: 2.4rem 0 1rem;
                    }
                    h1 { font-size: 2.4rem; }
                    h2 { font-size: 2rem; }
                    h3 { font-size: 1.55rem; }
                    blockquote {
                      border-left: 3px solid #dc2626;
                      background: #fafafa;
                      padding: 1rem 1.5rem;
                      margin: 2rem 0;
                      font-family: 'Instrument Serif', Georgia, serif;
                      font-style: italic;
                      font-size: 1.3rem;
                      color: #2a2a2a;
                      border-radius: 0 4px 4px 0;
                    }
                    a { color: #dc2626; border-bottom: 1px solid #dc2626; }
                    /* Images: centered horizontally, scale with width, preserve aspect ratio. */
                    img {
                      display: block !important;
                      margin: 2rem auto !important;
                      max-width: 100% !important;
                      height: auto !important;
                      border-radius: 4px;
                      float: none !important;
                    }
                    figure { margin: 2rem auto; text-align: center; }
                    figure img { margin: 0 auto !important; }
                    figcaption {
                      font-family: 'Instrument Serif', Georgia, serif;
                      font-style: italic;
                      color: #6b6b6b;
                      font-size: 0.95rem;
                      margin-top: 0.75rem;
                    }
                    ul, ol { padding-left: 1.5rem; margin: 0 0 1.4rem; }
                    li { margin-bottom: 0.5rem; }
                    li::marker { color: #dc2626; }
                    hr { border: 0; height: 1px; background: #ececec; margin: 2rem auto; width: 40%; }
                    code {
                      background: #fafafa; border: 1px solid #ececec;
                      padding: 2px 6px; border-radius: 3px;
                      color: #dc2626; font-size: 0.92em;
                    }
                    pre {
                      background: #0a0a0a; color: #fff;
                      padding: 1rem 1.25rem; border-radius: 4px; overflow-x: auto;
                    }
                    pre code { background: transparent; color: inherit; border: none; padding: 0; }
                  `,
                }}
              />
            </div>
          </div>

          <button type="submit" className="btn-save" disabled={busy}>
            {busy ? "Publishing..." : "Publish Project →"}
          </button>
        </form>
      </div>
    </div>
  );
}
